import React, { useState } from 'react';
import { Grid, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Slider, Chip } from '@mui/material';
import { Search, SlidersHorizontal } from 'lucide-react';
import { indianStates, indianCities } from '../../data/dummyData';

const selectSx = {
  fontFamily:'DM Sans,sans-serif', fontSize:13,
  background:'#fff', borderRadius:'12px !important',
  '& .MuiOutlinedInput-notchedOutline':{ borderColor:'var(--border)' },
  '&:hover .MuiOutlinedInput-notchedOutline':{ borderColor:'#d4c4b0' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline':{ borderColor:'var(--brand)' },
};
const labelSx = { fontSize:13, fontFamily:'DM Sans,sans-serif', color:'var(--text-muted)', '&.Mui-focused':{ color:'var(--brand)' } };
const inputSx = {
  '& .MuiOutlinedInput-root': { background:'#fff', borderRadius:'12px !important', fontSize:13, fontFamily:'DM Sans,sans-serif', '& fieldset':{ borderColor:'var(--border)' }, '&:hover fieldset':{ borderColor:'#d4c4b0' }, '&.Mui-focused fieldset':{ borderColor:'var(--brand)' } },
};
const chipSx = { background:'var(--brand-light)', color:'var(--brand)', border:'1px solid var(--brand-mid)', fontFamily:'DM Sans', fontSize:12, fontWeight:600 };

const mediaTypes = ['hoarding', 'billboard', 'digital_screen', 'flex_banner', 'wall_painting'];

export interface SearchFiltersValue {
  search: string;
  state: string;
  city: string;
  mediaType: string;
  minRent: number;
  maxRent: number;
}

export const defaultSearchFilters: SearchFiltersValue = {
  search: '', state: '', city: '', mediaType: '', minRent: 0, maxRent: 200000,
};

interface SearchFilterFormProps {
  /** Called only when the user clicks Search (or presses Enter in the text field), never on every keystroke/select change. */
  onSearch: (filters: SearchFiltersValue) => void;
}

export const SearchFilterForm: React.FC<SearchFilterFormProps> = ({ onSearch }) => {
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [priceRange, setPriceRange] = useState<number[]>([0, 200000]);
  const [showPrice, setShowPrice] = useState(false);

  const cities = state ? (indianCities[state] || []) : [];

  const runSearch = () => {
    onSearch({ search, state, city, mediaType, minRent: priceRange[0], maxRent: priceRange[1] });
  };

  const handleClear = () => {
    setSearch(''); setState(''); setCity(''); setMediaType(''); setPriceRange([0, 200000]);
    onSearch({ ...defaultSearchFilters });
  };

  const activeCount = [state, city, mediaType].filter(Boolean).length;

  return (
    <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'16px 20px', marginBottom:24, boxShadow:'var(--shadow-sm)' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth size="small" placeholder="Search title, area, city..."
            value={search} onChange={e=>setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
            sx={inputSx}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={14} color="var(--text-muted)"/></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel sx={labelSx}>State</InputLabel>
            <Select value={state} onChange={e=>{ setState(e.target.value); setCity(''); }} label="State" sx={selectSx}>
              <MenuItem value="" sx={{ fontSize:13, color:'var(--text-muted)' }}>All States</MenuItem>
              {indianStates.map(s=><MenuItem key={s} value={s} sx={{ fontSize:13 }}>{s}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <FormControl fullWidth size="small" disabled={!state}>
            <InputLabel sx={labelSx}>City</InputLabel>
            <Select value={city} onChange={e=>setCity(e.target.value)} label="City" sx={selectSx}>
              <MenuItem value="" sx={{ fontSize:13, color:'var(--text-muted)' }}>All Cities</MenuItem>
              {cities.map(c=><MenuItem key={c} value={c} sx={{ fontSize:13 }}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel sx={labelSx}>Media Type</InputLabel>
            <Select value={mediaType} onChange={e=>setMediaType(e.target.value)} label="Media Type" sx={selectSx}>
              <MenuItem value="" sx={{ fontSize:13, color:'var(--text-muted)' }}>All Types</MenuItem>
              {mediaTypes.map(t=><MenuItem key={t} value={t} sx={{ fontSize:13, textTransform:'capitalize' }}>{t.replace(/_/g,' ')}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={1}>
          <button type="button" onClick={()=>setShowPrice(!showPrice)} className="btn-ghost"
            style={{ width:'100%', padding:'9px 10px', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
            <SlidersHorizontal size={14}/>
          </button>
        </Grid>
        <Grid item xs={6} sm={3} md={1}>
          <button type="button" onClick={runSearch}
            style={{ width:'100%', background:'linear-gradient(135deg,#e8621a,#c2490e)', color:'#fff', border:'none', borderRadius:10, padding:'9px 10px', fontFamily:'DM Sans', fontWeight:600, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, boxShadow:'var(--shadow-brand)' }}>
            <Search size={14}/> Search
          </button>
        </Grid>

        {showPrice && (
          <Grid item xs={12}>
            <div style={{ paddingTop:8, paddingBottom:4 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                <span style={{ color:'var(--text-secondary)', fontSize:13, fontWeight:500 }}>Rent Range</span>
                <span style={{ color:'var(--brand)', fontSize:13, fontWeight:600 }}>₹{priceRange[0].toLocaleString('en-IN')} – ₹{priceRange[1].toLocaleString('en-IN')}</span>
              </div>
              <Slider value={priceRange} onChange={(_,v)=>setPriceRange(v as number[])} min={0} max={200000} step={5000}
                sx={{ color:'var(--brand)', '& .MuiSlider-rail':{ background:'var(--bg-muted)' }, '& .MuiSlider-thumb':{ background:'var(--brand)', boxShadow:'0 2px 8px rgba(232,98,26,0.4)' } }}/>
              <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>Click Search to apply the rent range.</div>
            </div>
          </Grid>
        )}

        {(state || city || mediaType) && (
          <Grid item xs={12}>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
              {state && <Chip label={state} onDelete={()=>setState('')} size="small" sx={chipSx}/>}
              {city && <Chip label={city} onDelete={()=>setCity('')} size="small" sx={chipSx}/>}
              {mediaType && <Chip label={mediaType.replace(/_/g,' ')} onDelete={()=>setMediaType('')} size="small" sx={{...chipSx, textTransform:'capitalize'}}/>}
              <span style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'DM Sans' }}>Click Search to apply changes</span>
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          <button type="button" onClick={handleClear}
            style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:12, fontFamily:'DM Sans', cursor:'pointer', textDecoration:'underline', padding:0 }}>
            Clear all filters
          </button>
        </Grid>
      </Grid>
    </div>
  );
};

export default SearchFilterForm;